import { CaseRunCalculateDetailModel } from "@app/api2/api";
import * as React from "react";
import { BPBoxWithHeader } from "@common/BPBoxWithHeader";
import { InfoBox } from "@common/InfoBox";
import { anyToDecimal, downloadBlob, isNullOrWhitespace, toSci, toSciDecimal } from "@common/Utils";
import { BPPlotly } from "@common/BPPlotly";
import { BPDataTable3 } from "../../Common/DataTable/BPDataTable3";

export interface ICalculationResultsProps {
    model: CaseRunCalculateDetailModel;
    forPrint?: boolean;
}

export function CalculationResults(props: ICalculationResultsProps) {
    return (<BPBoxWithHeader forPrint={props.forPrint} header={window.translation.common.results}>
        {props.model.integralJobResponse && <>
            <InfoBox forPrint={props.forPrint} compact header={window.translation.caseRunView.likelihood} items={[{
                width: 10,
                label: window.translation.caseRunView.likelihoodRatio,
                description: toSci(props.model.integralJobResponse.likelihoodRatio)
            }, {
                width: 10,
                label: <span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.likelihoodRatioLog10
                }} />,
                description: toSci(props.model.integralJobResponse.likelihoodRatioLog10)
            }]} />
            <InfoBox forPrint={props.forPrint} compact header={<span dangerouslySetInnerHTML={{
                __html: window.translation.caseRunView.hp
            }} />} items={[{
                width: 10,
                label: window.translation.caseRunView.likelihood,
                description: toSci(props.model.integralJobResponse.hp.likelihood)
            }, {
                width: 10,
                label: <span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.logLikelihood
                }} />,
                description: toSci(props.model.integralJobResponse.hp.logLikelihood)
            }, {
                width: 10,
                label: window.translation.caseRunView.evaluations,
                description: props.model.integralJobResponse.hp.evaluations
            }]} />
            <InfoBox forPrint={props.forPrint} compact header={<span dangerouslySetInnerHTML={{
                __html: window.translation.caseRunView.hd
            }} />} items={[{
                width: 10,
                label: window.translation.caseRunView.likelihood,
                description: toSci(props.model.integralJobResponse.hd.likelihood)
            }, {
                width: 10,
                label: <span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.logLikelihood
                }} />,
                description: toSci(props.model.integralJobResponse.hd.logLikelihood)
            }, {
                width: 10,
                label: window.translation.caseRunView.evaluations,
                description: props.model.integralJobResponse.hd.evaluations
            }]} />
        </>}
        {props.model.mcmcSensitivityJobResponse && <>
            {props.model.mcmcSensitivityJobResponse.likelihoods.map(m => <React.Fragment key={`mcmc_lik_${m.type}`}>
                <InfoBox forPrint={props.forPrint} compact header={window.translation.likelihoodType[m.type]} items={[
                    {
                        width: 10,
                        label: window.translation.caseRunView.likelihoodRatio,
                        description: toSci(m.likelihoodRatio)
                    },
                    {
                        width: 10,
                        label: <span dangerouslySetInnerHTML={{
                            __html: window.translation.caseRunView.likelihoodRatioLog10
                        }} />,
                        description: toSci(m.likelihoodRatioLog10)
                    },
                ]} />
                {m.type != "mcmc_sensitivity" && <InfoBox forPrint={props.forPrint} compact header={window.translation.caseRunView.likelihood} items={[{
                    width: 10,
                    label: <span dangerouslySetInnerHTML={{
                        __html: window.translation.caseRunView.hp
                    }} />,
                    description: m.hp != 0 ? toSci(m.hp) : toSciDecimal(anyToDecimal(m.hpLog10).exp())
                }, {
                    width: 10,
                    label: <span dangerouslySetInnerHTML={{
                        __html: window.translation.caseRunView.hd
                    }} />,
                    description: m.hd != 0 ? toSci(m.hd) : toSciDecimal(anyToDecimal(m.hdLog10).exp())
                }]} />}

            </React.Fragment>)}
            <InfoBox forPrint={props.forPrint} compact header={window.translation.caseRunView.samplingAcceptanceRate} items={[{
                width: 10,
                label: <span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.hp
                }} />,
                description: toSci(props.model.mcmcSensitivityJobResponse.hp.acceptanceRate)
            }, {
                width: 10,
                label: <span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.hd
                }} />,
                description: toSci(props.model.mcmcSensitivityJobResponse.hd.acceptanceRate)
            }]} />
            <BPBoxWithHeader forPrint={props.forPrint} compact header={window.translation.caseRunView.mcmcBurnIn}>
                <BPDataTable3 forPrint={props.forPrint} simple dataSource={() => Promise.resolve({
                    items: props.model.mcmcSensitivityJobResponse.burnInResults,
                    totalRows: 0
                })} columns={[{
                    accessor: 'niter',
                    Header: window.translation.caseRunView.mcmcNiter
                }, {
                    accessor: 'delta',
                    Header: window.translation.caseRunView.mcmcDelta,
                    Cell: cell => <span>{cell.value} ({toSci(cell.row.original.acceptanceRate / 0.25)})</span>
                }, {
                    accessor: 'acceptanceRate',
                    Header: window.translation.caseRunView.samplingAcceptanceRate
                }]} />

            </BPBoxWithHeader>
            <BPBoxWithHeader forPrint={props.forPrint} compact header={window.translation.caseRunView.mcmcBurnInHd}>
                <BPDataTable3 forPrint={props.forPrint} simple dataSource={() => Promise.resolve({
                    items: props.model.mcmcSensitivityJobResponse.burnInResultsHd,
                    totalRows: 0
                })} columns={[{
                    accessor: 'niter',
                    Header: window.translation.caseRunView.mcmcNiter
                }, {
                    accessor: 'delta',
                    Header: window.translation.caseRunView.mcmcDelta,
                    Cell: cell => <span>{cell.value} ({toSci(cell.row.original.acceptanceRate / 0.25)})</span>
                }, {
                    accessor: 'acceptanceRate',
                    Header: window.translation.caseRunView.samplingAcceptanceRate
                }]} />

            </BPBoxWithHeader>
            {props.model.mcmcTraceGraphUrl && <>
                <BPBoxWithHeader forPrint={props.forPrint} compact header={window.translation.caseRunView.mcmcTracePlot}>
                    <img style={{ maxWidth: '100%' }} alt="" src={props.model.mcmcTraceGraphUrl} />
                </BPBoxWithHeader>
            </>}
            {props.model.mcmcSensGraphUrl && <>
                <BPBoxWithHeader forPrint={props.forPrint} compact header={window.translation.caseRunView.mcmcSensitivity}>
                    <img style={{ maxWidth: '100%' }} alt="" src={props.model.mcmcSensGraphUrl} />
                </BPBoxWithHeader>
            </>}

        </>}
        {props.model.ncaGraphUrl && <>
            <InfoBox forPrint={props.forPrint} compact header={window.translation.caseRunView.ncAnalysis} items={[{
                width: 10,
                label: window.translation.caseRunView.ncAnalysisMax,
                description: toSci(props.model.ncaMax)
            }, {
                width: 10,
                label: window.translation.caseRunView.ncAnalysisLogMax,
                description: toSci(props.model.ncaLogMax)
            }, {
                width: 10,
                label: window.translation.caseRunView.ncAnalysisMeanLR,
                description: toSci(props.model.ncaMeanLR)
            }]} />
            {props.model.ncaGraphUrl ?
                <BPBoxWithHeader forPrint={props.forPrint} compact header={window.translation.caseRunView.ncAnalysis}
                    buttons={props.model.ncaLRRM ? [{
                            label: 'Download LRs', action: () => {
                                downloadBlob({ data: new Blob(["Log10LR\tLR\n" + props.model.ncaLRRM.map(lr => `${lr}\t${Math.pow(10, lr)}`).join('\n')], { type: 'plain/text' }), fileName: `${props.model.caseName}_nca_lrr.csv` });
                            }}] : null}>
                    <img style={{ maxWidth: '100%' }} alt="" src={props.model.ncaGraphUrl} />
                </BPBoxWithHeader> : null}
        </>}
        {/* {props.model.modelFittedJobResponse && <> */}

        {props.model.hpFittedModelGraphUrl || props.model.hpFittedModelPlotlyUrl ? <BPBoxWithHeader forPrint={props.forPrint} compact header={<span dangerouslySetInnerHTML={{ __html: window.translation.caseRunView.hpFittedModel }}></span>}>
            {/* {!isNullOrWhitespace(props.model.hpFittedModelGraphUrl) && <img style={{ maxWidth: '100%' }} alt="" src={props.model.hpFittedModelGraphUrl} />} */}
            {props.model.hpFittedModelPlotlyUrl && <BPPlotly autoWidth={true} jsonUrl={props.model.hpFittedModelPlotlyUrl} title={window.translation.caseRunView.epgOverlay} />}
        </BPBoxWithHeader> : null}
        {props.model.hdFittedModelGraphUrl || props.model.hdFittedModelPlotlyUrl ? <BPBoxWithHeader forPrint={props.forPrint} compact header={<span dangerouslySetInnerHTML={{ __html: window.translation.caseRunView.hdFittedModel }}></span>}>
            {/* <img style={{ maxWidth: '100%' }} alt="" src={props.model.hdFittedModelGraphUrl} /> */}
            {props.model.hdFittedModelPlotlyUrl && <BPPlotly autoWidth={true} jsonUrl={props.model.hdFittedModelPlotlyUrl} title={window.translation.caseRunView.epgOverlay} />}
        </BPBoxWithHeader> : null}
        {/* </>} */}
        {!isNullOrWhitespace(props.model.hpModelValidationGraphUrl) ? <BPBoxWithHeader forPrint={props.forPrint} compact header={'Hp'}><img style={{ maxWidth: '100%' }} alt="" src={props.model.hpModelValidationGraphUrl} /></BPBoxWithHeader> : null}
        {!isNullOrWhitespace(props.model.hdModelValidationGraphUrl) ? <BPBoxWithHeader forPrint={props.forPrint} compact header={'Hd'}><img style={{ maxWidth: '100%' }} alt="" src={props.model.hdModelValidationGraphUrl} /></BPBoxWithHeader> : null}


        {/* {props.model.deconvolutionJobResponse && <>
            <Tab panes={[{
                menuItem: <Menu.Item><span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.hp
                }}></span></Menu.Item>,
                render: () => <TabPane><Deconvolution model={{
                    type: 'p',
                    allJointSystems: props.model.deconvolutionJobResponse.hp.allJoint,
                    // allMarginalContributors: model.deconvolutionJobResponse.hp.allMarginalGenotypes,
                    topMarginalSystems: props.model.deconvolutionJobResponse.hp.topMarginal
                }} /></TabPane>
            }, {
                menuItem: <Menu.Item><span dangerouslySetInnerHTML={{
                    __html: window.translation.caseRunView.hd
                }}></span></Menu.Item>,
                render: () => <TabPane> <Deconvolution model={{
                    type: 'd',
                    allJointSystems: props.model.deconvolutionJobResponse.hd.allJoint,
                    // allMarginalContributors: model.deconvolutionJobResponse.hd.allMarginalGenotypes,
                    topMarginalSystems: props.model.deconvolutionJobResponse.hd.topMarginal
                }} /></TabPane>
            }]}></Tab>
        </>} */}
    </BPBoxWithHeader>);
}
